/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo } from 'react';
import TenantUsersContext from './TenantUsersContext';
import initialTenantUsersState from './initialTenantUsersState';
import tenantsUsersReducer from 'contexts/tenantUsers/reducers/tenantUsers.reducer';
import * as action from 'contexts/tenantUsers/reducers/tenantUsers.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import useContextUser from 'hooks/contexts/useContextUser';
import useContextSuperAdmin from 'hooks/contexts/useContextSuperAdmin';
import { IChildrenProps } from 'interfaces/general.interface';
import useContextCompany from 'hooks/contexts/useContextCompany';
import {
  ITenantUser,
  IcreateUpdateTenantUserProps,
  IupdateTenantUserActionProps,
} from 'interfaces/tenantUsers.interface';

function TenantUsersProvider(props: IChildrenProps) {
  const [state, dispatch] = useReducer(tenantsUsersReducer, initialTenantUsersState);
  const { getAccessTokenSilently } = useAuth0();
  const { t: translate } = useTranslation();
  const { dbUser } = useContextUser();
  const { company } = useContextCompany();
  const { addUserClient } = useContextSuperAdmin();

  const getTenantUsers = useCallback(async () => {
    if (!company?.id || !dbUser?.role) return false;
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.getTenantUsersAction({
      dispatch,
      tenantId: company.id,
      userRole: dbUser.role,
      token,
      translate,
    });
  }, [company?.id, dbUser?.role]);

  const selectTenantUser = useCallback((user: ITenantUser | null) => {
    action.selectTenantUserAction({ dispatch, user });
  }, []);

  const createTenantUser = useCallback(
    async (newTenantUser: IcreateUpdateTenantUserProps) => {
      if (!company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.createTenantUserAction({
        dispatch,
        tenantId: company.id,
        newTenantUser,
        token,
        translate,
        addUserClient,
      });
    },
    [company?.id, addUserClient]
  );

  const updateTenantUser = useCallback(
    async ({ user, updateRole, prevRole }: IupdateTenantUserActionProps) => {
      if (!state.tenantUsersSelected) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateTenantUserAction({
        dispatch,
        user,
        userId: state.tenantUsersSelected.key,
        updateRole,
        prevRole,
        token,
        translate,
      });
    },
    [state.tenantUsersSelected]
  );

  const deleteTenantUser = useCallback(async (userId: string) => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.deleteTenantUserAction({ dispatch, userId, token, translate });
  }, []);

  const memoProvider = useMemo(
    () => ({
      ...state,
      getTenantUsers,
      selectTenantUser,
      createTenantUser,
      updateTenantUser,
      deleteTenantUser,
    }),
    [
      state,
      getTenantUsers,
      selectTenantUser,
      createTenantUser,
      updateTenantUser,
      deleteTenantUser,
    ]
  );

  return (
    <TenantUsersContext.Provider value={memoProvider}>
      {props.children}
    </TenantUsersContext.Provider>
  );
}

export default memo(TenantUsersProvider);
